import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import { TradingExecutionRoute } from '@ebalance/proto/bfftypes/v1/trading_execution_route_pb'
import type { Supply48DataSource } from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'
import { KOMA_COUNT } from '~/constants/koma'
import type { GateCloseFrontier } from '~/models/gateClose'
import { isInRange } from '~/models/gridSelection'
import { KOMA_DIVIDER_BORDER, isKomaDividerAfter } from '~/models/komaDivider'
import { KOMA_TIME_LABELS } from '~/models/komaToTime'
import { GridCell } from '~/pages/Supply48Data/components/GridCell'
import { RevertButton } from '~/pages/Supply48Data/components/RevertButton'
import type { GridCellInfo } from '~/pages/Supply48Data/hooks/useSupply48GridKeyboard'
import { useSupply48GridKeyboard } from '~/pages/Supply48Data/hooks/useSupply48GridKeyboard'
import type {
  CellInputRejectionReport,
  RawRejectionReport,
  SaveErrorReason,
} from '~/pages/Supply48Data/models/cellValue'
import {
  parseCellValue,
  saveErrorTooltipKey,
} from '~/pages/Supply48Data/models/cellValue'
import { isOverContractThreshold } from '~/pages/Supply48Data/models/contractCheck'
import { protoDateToShortLabel } from '~/pages/Supply48Data/models/dateUtils'
import type { DisplayUnit } from '~/pages/Supply48Data/models/displayUnit'
import {
  displayUnitLabelKey,
  toDisplayValue,
  toStoredValue,
} from '~/pages/Supply48Data/models/displayUnit'
import type { CellEdit } from '~/pages/Supply48Data/models/editHistory'
import type { EditStateMap } from '~/pages/Supply48Data/models/editState'
import {
  isKomaEditing,
  isKomaManual,
  isKomaSavedManual,
  makeEditRowKey,
  makeSaveErrorCellKey,
} from '~/pages/Supply48Data/models/editState'
import { isCellLocked } from '~/pages/Supply48Data/models/gcLock'
import {
  displaySlotValue,
  toDateKey,
  underlyingSlotValue,
} from '~/pages/Supply48Data/models/gridRows'
import {
  COLOR_BORDER_LOW,
  COLOR_TEXT_MID,
  S48_DATA_COL_WIDTH_PX,
  STICKY_COL_STYLES,
  thGroupStyles,
} from '~/pages/Supply48Data/models/gridStyles'
import type { DayIndex } from '~/pages/Supply48Data/models/saveRows'
import { sourceDisplayLabel } from '~/pages/Supply48Data/models/sourceDisplayLabel'
import {
  isEditableSource,
  isMarketSource,
} from '~/pages/Supply48Data/models/sourceKind'
import {
  tradeTypeLabelKey,
  tradeTypeOf,
} from '~/pages/Supply48Data/models/tradeType'

/**
 * 取引 source の表示属性 (公開取引 ID・契約電力量・成約ID・約定経路) を
 * oneof detail.contract から取り出す (supply-48-source-redesign: 旧 Supply48DataLeg
 * のフラット field から移設)。表示名 (取引先名 / 市場 market_kind ラベル) は
 * `sourceDisplayLabel` (models/sourceDisplayLabel.ts) に集約したためここでは
 * 持たない。契約以外の source type (市場等) は空文字 / undefined にフォールバックする。
 */
function contractDetail(source: Supply48DataSource): {
  publicTradeId: string
  contractVolumeMw: string | undefined
  dealId: string
  executionRoute: TradingExecutionRoute
} {
  if (source.detail.case === 'contract') {
    return {
      publicTradeId: source.detail.value.publicTradeId,
      contractVolumeMw: source.detail.value.contractVolumeMw?.value,
      dealId: source.detail.value.dealId,
      executionRoute: source.detail.value.executionRoute,
    }
  }
  return {
    publicTradeId: '',
    contractVolumeMw: undefined,
    dealId: '',
    executionRoute: TradingExecutionRoute.UNSPECIFIED,
  }
}

/**
 * 2 段ヘッダのグループ行 (1 行目) の高さ。sub 列ヘッダ (2 行目) の sticky top
 * オフセットと一致させる (= sticky 2 段ヘッダはブラウザが行高を連動してくれない
 * ため、固定高で両者を同期する)。
 */
/*
 * 30 → 32 (2026-08-28)。 TH を 8080 準拠の 14px にした結果、 30px 指定でも
 * 中身が収まらず **実測 31px** に溢れ、 sub-row の sticky top (この定数) と
 * 1px ずれて 取引先ヘッダが上に食い込んでいた (デザイナー指摘)。
 * Chromium 実測: font 14px / padding 4px 8px / border-bottom 2px のとき
 * 30px 指定 → 31px、 **32px 指定 → 32px** (指定どおり収まる)。
 */
const GROUP_HEADER_ROW_HEIGHT = 32

/** 表の下端に残す余白 (px)。 画面下にぴったり付けず 1 段の余白を残す。 */
const GRID_BOTTOM_GAP = 16

/**
 * 選択中区分 → グループヘッダ名の i18n キー。フィルタの区分ラベルと同一文言を
 * 再利用する (= 画面内で同じ概念に別文言を作らない)。
 *
 * 常時BU は AC-S4D-001 で相対取引カテゴリへ統合され、区分としては選べなくなったので
 * 分岐を持たない (= 万一渡っても相対取引として表示され、統合後の意味と一致する)。
 */
function categoryLabelKey(category: SupplyCategory): string {
  switch (category) {
    case SupplyCategory.MARKET:
      return 'page:supply48Data.filter.categoryMarket'
    case SupplyCategory.FUTURES:
      return 'page:supply48Data.filter.categoryFutures'
    default:
      return 'page:supply48Data.filter.categoryBilateral'
  }
}

/** §8: chartTargetLegId は string | null (legs 構成変化で別 leg を誤表示する事故防止) */
type Props = {
  legs: Supply48DataSource[]
  /** 選択中の区分。2 段ヘッダ 1 行目のグループ名表示に使う (issue #3659) */
  category: SupplyCategory
  gateClose: GateCloseFrontier | undefined
  editState: EditStateMap
  /** §2: dayIndex は page で 1 回だけ useMemo 構築して渡す */
  dayIndex: DayIndex
  /**
   * workflow-back-bar-readonly AC-18: ワークフローケースが完了済み/削除済みのとき true。
   * true のときセル編集・列単位の元に戻すを disabled にする（表示切替系ではなく編集・確定操作のため）。
   *
   * AC-S4D-003 で編集モードトグルを廃止したため、セルの編集可否はこの readonly と
   * GC ロックだけで決まる (= ロックされていないセルは常時直接編集できる)。
   */
  isReadonly?: boolean
  /**
   * 表示単位 (AC-S4D-037)。セルの表示値・入力値はこの単位で、
   * 保存値は常に kWh/30min。換算は本コンポーネントの境界で閉じる。
   */
  displayUnit: DisplayUnit
  /**
   * AC-S4D-008: legId → その供給源の手動変更コマ数 (0 件の leg は entry なし)。
   * 手動変更が 1 件以上ある列だけに列単位リセットを出すために使う。
   */
  manualEditCountByLeg: Map<string, number>
  /** §8: グラフ対象 legId。null = グラフ非表示 */
  chartTargetLegId: string | null
  /**
   * §5 バグB修正: 表示期間の ProtoDate 一覧 (行の描画順 = revert の走査対象)。
   * #4338 で手動変更フィルタを撤去したため、渡された日付をそのまま全行描画する。
   */
  visibleDates: ProtoDate[]
  /**
   * セルへの書き込み (AC-S4D-003 のインライン編集 / 貼付 / 一括入力 / クリア)。
   * 1 回の呼び出しが undo 1 回分になる。coalesceKey が同じ連続呼び出しは畳まれる。
   */
  onCellEdits: (edits: CellEdit[], coalesceKey?: string) => void
  /**
   * 1 操作分の入力拒否レポート (AC-S4D-041 / AC-S4D-042)。拒否ゼロの操作でも呼ぶため、
   * 呼び出し側は「直前のエラーを解除する」用途にもそのまま使える。
   */
  onCellInputRejections: (report: CellInputRejectionReport) => void
  /** Ctrl+Z / Ctrl+Y (Ctrl+Shift+Z) */
  onUndo: () => void
  onRedo: () => void
  /** 列ヘッダクリック時 (グラフ対象列切替) */
  onLegHeaderClick?: (legId: string) => void
  /**
   * 列単位で自動計算値に戻す (legId を渡す)
   */
  onRevertLegToAuto?: (legId: string) => void
  /**
   * AC-S4D-049: 「エラー（保存がバリデーションで拒否された）」セルの Map
   * (`makeSaveErrorCellKey` の結果 → 拒否理由)。 直前の保存試行 (Save 押下)
   * 時点のスナップショットで、 page 側 (index.tsx の `handleSave`) が
   * `collectSaveErrorCells` で作る (docstring 参照。1 打鍵ごとには作らない)。
   */
  saveErrorCells: ReadonlyMap<string, SaveErrorReason>
  /**
   * AC-S4D-052: 遷移直後の自動横スクロール・一時ハイライトの対象列 (legId =
   * supply48SourceId)。解決と一度きりの判定はページ側の
   * `useSupply48ScrollHighlight` が持ち、グリッドは受け取った legId の列へ
   * scrollIntoView してハイライトを描くだけを担う。null / 省略なら何もしない
   * (通常遷移・直接アクセスとの互換)。
   */
  scrollHighlightedLegId?: string | null
  /**
   * AC-S4D-060: 入力漏れの可能性がある非定型の取引の列 (legId)。見出しに
   * 「⚠ 未入力」の印を出す。判定はページ側 (`findInputMissingSources`)。省略なら出さない。
   */
  inputMissingLegIds?: ReadonlySet<string>
  /**
   * 非定型の取引の列 (差金決済タブの列は除く)。page が `nonStandardLegIds` で導出した集合で、
   * 値が同じでも手動変更の印が違うコマを「編集中」にする (AC-S4D-060)。
   */
  nonStandardLegIds: ReadonlySet<string>
}

/**
 * 48点データのメイングリッドコンポーネント。
 *
 * レイアウト:
 *   行 = 受渡日 × 48 コマ (日付 × コマ番号)
 *   列 = 日付 (固定) / コマ (固定) / Leg ごとの値
 *
 * 生 <table> を使用する理由:
 *   セル単位のキーボード操作 (Tab/Enter/Delete/Ctrl+Enter)、
 *   sticky ヘッダ + 日付列の rowSpan(48)、
 *   仮想化を将来導入する際の素地として生 table が必要。
 *   ecloud の Table コンポーネントはこれらの Excel 風操作に対応していない。
 */
export const Supply48DataGrid = ({
  legs,
  category,
  gateClose,
  editState,
  dayIndex,
  isReadonly = false,
  displayUnit,
  manualEditCountByLeg,
  chartTargetLegId,
  visibleDates,
  onCellEdits,
  onCellInputRejections,
  onUndo,
  onRedo,
  onLegHeaderClick,
  onRevertLegToAuto,
  saveErrorCells,
  scrollHighlightedLegId = null,
  inputMissingLegIds,
  nonStandardLegIds,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  /** legId → th (sub 列ヘッダ) DOM 要素。 scrollIntoView の呼び出し先解決に使う。 */
  const legHeaderRefs = useRef<Map<string, HTMLTableCellElement>>(new Map())
  useEffect(() => {
    if (scrollHighlightedLegId === null) return
    const el = legHeaderRefs.current.get(scrollHighlightedLegId)
    // テスト環境 (happy-dom) では scrollIntoView は実スクロールを伴わない
    // no-op のため、テストは spy して呼び出しのみ検証する
    // (Supply48DataGrid.test.tsx「遷移直後の自動横スクロール」参照)。
    el?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }, [scrollHighlightedLegId])

  /*
   * AC-S4D-003 (#4340): 編集モードトグルは廃止し、ロックされていないセルは
   * 常時直接編集できる (需要想定画面と同一の操作体系)。書き込みを止めるのは
   * workflow-back-bar-readonly AC-18 の read-only と GC ロックだけ。
   */
  const canEdit = !isReadonly

  /*
   * AC-S4D-001 (issue #5129): 先物カテゴリは「保有量確認のみ」で、値を編集
   * できない。先物の行は市場と異なり CONTRACT source (isEditableSource の
   * allow-list を素通りする — SupplyCategoryLegFilterFor が先物も同じ leg
   * テーブルから引くため) なので、書き込み拒否は source 種別ではなく選択中
   * 区分で判定する。選択中の全 leg は List のカテゴリ絞り込みにより必ず
   * 先物なので、category 単位の判定で足りる (市場列の isMarketSource と同じ
   * 「常時 read-only」表現を流用する)。
   */
  const isFuturesCategory = category === SupplyCategory.FUTURES

  /**
   * グリッド座標 → セル情報。キーボード操作 (移動 / 範囲選択 / 貼付) は
   * すべてこの写像を通す。row = 表示日 index × 48 + コマ index、col = leg index。
   */
  const cellAt = useCallback(
    (row: number, col: number): GridCellInfo | undefined => {
      if (row < 0 || col < 0) return undefined
      const date = visibleDates[Math.floor(row / KOMA_COUNT)]
      const leg = legs[col]
      if (date === undefined || leg === undefined) return undefined
      const komaIdx = row % KOMA_COUNT
      const koma = komaIdx + 1
      const rowKey = makeEditRowKey(
        leg.supply48SourceId,
        date.year,
        date.month,
        date.day,
      )
      return {
        legId: leg.supply48SourceId,
        date,
        koma,
        // AC-S4D-032: 編集可能でない source (allow-list、現状 CONTRACT のみ) は
        // GC 状態に関わらず常時 read-only。AC-S4D-001: 先物カテゴリも
        // (source 種別に関わらず) 常時 read-only。
        editable:
          canEdit &&
          !isFuturesCategory &&
          !isCellLocked(gateClose, dayIndex.get(rowKey), date, koma) &&
          isEditableSource(leg),
        // hook は表示単位で動く (コピーは表示値基準、Escape 復帰は
        // underlyingSlotValue の素の値基準 — originValue のコメント参照)
        value: toDisplayValue(
          displaySlotValue(
            dayIndex.get(rowKey),
            editState.get(rowKey)?.slots,
            komaIdx,
          ),
          displayUnit,
        ),
        originValue: toDisplayValue(
          underlyingSlotValue(
            dayIndex.get(rowKey),
            editState.get(rowKey)?.slots,
            komaIdx,
          ),
          displayUnit,
        ),
        // 非定型の取引の列だけ、Escape の復元先に focus 時点の印を持つ (AC-S4D-060)
        originMark: nonStandardLegIds.has(leg.supply48SourceId)
          ? isKomaManual(
              editState.get(rowKey),
              dayIndex.get(rowKey)?.manualEditKomas ?? [],
              koma,
            )
          : undefined,
      }
    },
    [
      visibleDates,
      legs,
      canEdit,
      isFuturesCategory,
      gateClose,
      dayIndex,
      editState,
      displayUnit,
      nonStandardLegIds,
    ],
  )

  /**
   * 表示単位のセル入力パーサ。整数以外を理由付きで弾く。
   * kW モードの偶数制約 (AC-S4D-028) は打鍵の途中経過を壊さないよう保存時に見る。
   */
  const parseValue = useCallback((raw: string) => parseCellValue(raw), [])

  /**
   * 表示単位 → 保存単位 (kWh/30min) の換算をここで閉じる。
   * 換算を挟まないと kW モードの編集が表示値のまま永続化され、値が黙って倍になる。
   */
  const handleCellEdits = useCallback(
    (edits: CellEdit[], coalesceKey?: string) => {
      onCellEdits(
        edits.map((edit) => ({
          ...edit,
          value:
            edit.value === null ? null : toStoredValue(edit.value, displayUnit),
        })),
        coalesceKey,
      )
    },
    [onCellEdits, displayUnit],
  )

  /** hook が返す座標ベースの拒否情報を、エラー文言に出せるラベルへ写す */
  const handleRejections = useCallback(
    (report: RawRejectionReport) => {
      // 貼付 (AC-S4D-042) は件数だけの通知なので座標→ラベルの解決が要らない
      if (report.kind === 'pasteSkipped') {
        onCellInputRejections(report)
        return
      }
      onCellInputRejections({
        kind: 'cells',
        rejections: report.rejections.map((rejection) => {
          const date = visibleDates[Math.floor(rejection.row / KOMA_COUNT)]
          const leg = legs[rejection.col]
          return {
            reason: rejection.reason,
            raw: rejection.raw,
            itemName: sourceDisplayLabel(leg, t),
            dateLabel: date !== undefined ? protoDateToShortLabel(date) : '',
            komaLabel: KOMA_TIME_LABELS[rejection.row % KOMA_COUNT] ?? '',
          }
        }),
      })
    },
    [visibleDates, legs, onCellInputRejections, t],
  )

  const gridRef = useRef<HTMLDivElement | null>(null)

  /*
   * 表の高さを**実測ベース**で決める (デザイナー指摘 2026-08-28
   * 「常にブラウザ縦いっぱいに表示できないか」)。
   *
   * design は `.table-48-wrap { max-height: calc(100vh - 240px) }` と固定値だが、
   * 240px は proto の header / toolbar 高さに合わせた数字で、 app では
   * breadcrumb・ツールバー・info Alert の有無で上端位置が変わるため合わない
   * (実測で画面下に 100px 以上の死に領域が出ていた)。
   * 自分の viewport 上端位置を測って残り全部を使う。 alert の出し入れや
   * ウィンドウリサイズにも追随させる。
   */
  const [gridMaxHeight, setGridMaxHeight] = useState('calc(100vh - 240px)')
  useLayoutEffect(() => {
    const el = gridRef.current
    if (el === null) return
    const update = (): void => {
      const top = el.getBoundingClientRect().top
      // ページ自体がスクロールしている間は上端が動くので測り直さない
      // (収束後は表の内側だけがスクロールするため top は固定される)。
      if (top <= 0) return
      const topPx = String(Math.round(top))
      const gapPx = String(GRID_BOTTOM_GAP)
      setGridMaxHeight(`calc(100vh - ${topPx}px - ${gapPx}px)`)
    }
    update()
    window.addEventListener('resize', update)
    const observer = new ResizeObserver(update)
    observer.observe(document.body)
    return () => {
      window.removeEventListener('resize', update)
      observer.disconnect()
    }
  }, [])

  const { selection, containerHandlers, onCellInputChange } =
    useSupply48GridKeyboard({
      containerRef: gridRef,
      rowCount: visibleDates.length * KOMA_COUNT,
      colCount: legs.length,
      cellAt,
      enabled: canEdit,
      parseValue,
      onRejections: handleRejections,
      onCellEdits: handleCellEdits,
      onUndo,
      onRedo,
    })

  if (visibleDates.length === 0) {
    return (
      <Box padding={8} textAlign="center">
        <Text color="Text.midEmphasis" fontSize="sm">
          {t('page:supply48Data.grid.empty')}
        </Text>
      </Box>
    )
  }

  return (
    /*
     * キーボード / clipboard 操作 (AC-S4D-003) はセル input からの委譲でここが
     * まとめて受ける。セル位置は input の data-row / data-col から解決するため、
     * セルごとに closure を作らずに済み GridCell の memo が効く。
     */
    <Box
      ref={gridRef}
      overflow="auto"
      maxHeight={gridMaxHeight}
      borderWidth={1}
      borderColor="Border.lowEmphasis"
      {...containerHandlers}
    >
      {/*
       * 生 <table> を使用する理由:
       * セル単位キーボード操作 (Tab/Enter/Delete/Ctrl+Enter)、
       * sticky ヘッダ + 日付列 rowSpan(48)、将来の仮想化素地のため。
       * ecloud Table コンポーネントはこれらの Excel 風操作に非対応。
       */}
      <table
        style={{
          width: 'max-content',
          // #4343 (design/supply/48data.html:76-83): minWidth:'100%' を外す。
          // 列が少ないと余った幅を列が山分けして間延びするため、余剰幅は
          // テーブル右側の余白として残す (= 表を引き伸ばさない・左詰め)。
          borderCollapse: 'separate',
          borderSpacing: 0,
          // 8080 `.table-48 thead tr.sub-row th { font-size: var(--font-size-md) }`
          // に揃える (デザイナー指摘 2026-08-28「table の TH を可能な限り DS 準拠」)。
          // 表の基準サイズなので table 側で md にし、 個別に sm を撒かない。
          fontSize: fontSizes.md.value,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <thead>
          {/*
           * 2 段グループヘッダ (issue #3659、design/supply/48data.html 準拠):
           * 1 行目 = 日付・コマ (rowSpan=2) + 選択中区分名 (colSpan=leg 数)
           * 2 行目 = 供給源 (leg) ごとの sub 列ヘッダ
           */}
          <tr>
            {/*
             * 凍結ヘッダー (日付・コマ) は thGroupStyles を先に spread する。
             * 後置すると thGroupStyles 内の zIndex:3 が凍結用の zIndex:5 を
             * 上書きし、横スクロール時にグループ/leg ヘッダー (z=3, DOM 後続)
             * が凍結ヘッダーの上に描画されて覆い隠す (= 押し出されて見える)
             */}
            <th
              rowSpan={2}
              style={{
                ...thGroupStyles(COLOR_TEXT_MID, COLOR_BORDER_LOW),
                ...STICKY_COL_STYLES,
                left: 0,
                width: 64,
                minWidth: 64,
                zIndex: 5,
              }}
            >
              {t('page:supply48Data.grid.colDate')}
            </th>
            <th
              rowSpan={2}
              style={{
                ...thGroupStyles(COLOR_TEXT_MID, COLOR_BORDER_LOW),
                ...STICKY_COL_STYLES,
                left: 64,
                width: 60,
                minWidth: 60,
                zIndex: 5,
              }}
            >
              {t('page:supply48Data.grid.colKoma')}
            </th>
            {legs.length > 0 && (
              <th
                colSpan={legs.length}
                style={{
                  ...thGroupStyles(COLOR_TEXT_MID, COLOR_BORDER_LOW),
                  height: GROUP_HEADER_ROW_HEIGHT,
                  boxSizing: 'border-box',
                  padding: '4px 8px',
                }}
              >
                {/*
                 * design .s48-group-label 準拠: ラベルを横方向にも sticky にし、
                 * 横スクロール時も凍結列 (日付 64px + コマ 60px = 124px) の右に
                 * 区分名が視認され続けるようにする
                 */}
                <span
                  style={{
                    position: 'sticky',
                    left: 124,
                    display: 'inline-block',
                    maxWidth: '100%',
                  }}
                >
                  {/* AC-S4D-038: 現在の表示単位を表の見出しに明示する */}
                  {t('page:supply48Data.grid.groupHeaderWithUnit', {
                    category: t(categoryLabelKey(category)),
                    unit: t(displayUnitLabelKey(displayUnit)),
                  })}
                </span>
              </th>
            )}
          </tr>
          {/* sub 列ヘッダ行: Leg (source) ごと */}
          <tr>
            {legs.map((leg) => {
              const isChartTarget = chartTargetLegId === leg.supply48SourceId
              // AC-S4D-052: 遷移直後の一時列ハイライト対象か。
              const isScrollHighlighted =
                scrollHighlightedLegId === leg.supply48SourceId
              const isMarket = isMarketSource(leg)
              const { publicTradeId, dealId, executionRoute } =
                contractDetail(leg)
              // AC-S4D-001: 常時BU は独立タブにせず、行の取引種別として出す
              const tradeType = tradeTypeOf(executionRoute)
              const tradeTypeLabel = t(tradeTypeLabelKey(tradeType))
              // AC-S4D-004/040: 表示名導出は sourceDisplayLabel に集約 (契約=
              // 取引先名 / 市場=market_kind ラベル)。AC-S4D-032: 市場列は
              // market_kind ラベルのみ (取引先名・成約ID・取引種別を持たない —
              // 市場行は N 約定の集約で単一 DealId が構造的に不在、Decisions #5)。
              const label = sourceDisplayLabel(leg, t)
              return (
                <HintTooltip
                  key={leg.supply48SourceId}
                  content={
                    isMarket
                      ? label
                      : t('page:supply48Data.grid.legHeaderTitle', {
                          dealId,
                          partner: label,
                          publicTradeId,
                          tradeType: tradeTypeLabel,
                        })
                  }
                >
                  <th
                    ref={(el) => {
                      // AC-S4D-052: scrollIntoView の呼び出し先を legId で引けるように登録する。
                      if (el !== null) {
                        legHeaderRefs.current.set(leg.supply48SourceId, el)
                      } else {
                        legHeaderRefs.current.delete(leg.supply48SourceId)
                      }
                    }}
                    colSpan={1}
                    data-scroll-highlighted={isScrollHighlighted || undefined}
                    onClick={
                      onLegHeaderClick !== undefined
                        ? () => onLegHeaderClick(leg.supply48SourceId)
                        : undefined
                    }
                    style={{
                      ...thGroupStyles(COLOR_TEXT_MID, COLOR_BORDER_LOW),
                      position: 'sticky',
                      // 1 行目 (グループ行) の直下に貼り付く (2 段 sticky ヘッダ)
                      top: GROUP_HEADER_ROW_HEIGHT,
                      zIndex: 3,
                      // #4343 (design/supply/48data.html:138-139): データ td と同じ
                      // 明示幅を与える。sub 列ヘッダ th と データ td の両方に効かせないと
                      // auto layout が広い方 (= input の既定 intrinsic 幅) を採ってしまう。
                      width: S48_DATA_COL_WIDTH_PX,
                      // overflow:hidden を置くと th 自身が sticky の基準になり
                      // ラベルの横 sticky (left:124) が外側スクローラーに効かなくなる。
                      // th 自体は whiteSpace:nowrap のままにし、はみ出し制御
                      // (AC-S4D-039a: 長い略称は省略せず2行見出しの中で折り返す)
                      // は内側 Text の whiteSpace:normal + overflowWrap:anywhere
                      // が th の nowrap を上書きして折り返す形で担う
                      whiteSpace: 'nowrap',
                      padding: '4px 6px',
                      cursor:
                        onLegHeaderClick !== undefined ? 'pointer' : 'default',
                      // AC-S4D-052: 遷移直後の一時列ハイライトは、専用 design token
                      // が無いため既存のグラフ対象列ハイライト (isChartTarget) と
                      // 同じ視覚表現を流用する (EN/Des 判断)。
                      ...(isChartTarget || isScrollHighlighted
                        ? {
                            background:
                              LightModeSemanticColors.Surface
                                .accentPrimaryLightMid.value,
                            color:
                              LightModeSemanticColors.Text.accentPrimary.value,
                            boxShadow: `inset 0 -2px 0 0 ${LightModeSemanticColors.Border.accentPrimary.value}`,
                          }
                        : {}),
                    }}
                    // AC-S4D-052: テストから一時ハイライト状態を観測する seam
                    // (`data-scroll-highlighted`)。aria 属性は th の暗黙 role に
                    // 許可されないため data 属性にする (GridCell の data-selected と同じ扱い)。
                    title={
                      isMarket
                        ? label
                        : t('page:supply48Data.grid.legHeaderTitle', {
                            dealId,
                            partner: label,
                            publicTradeId,
                            tradeType: tradeTypeLabel,
                          })
                    }
                  >
                    {/*
                     * グループ名ラベルと同じ横 sticky (left: 凍結列 124px)。
                     * 列が凍結境界を跨いでいる間は leg 名が視認され続け、
                     * 列が過ぎるとセルと共に押し出される (sticky は自セル内に閉じる)
                     */}
                    <Flex
                      position="sticky"
                      left="124px"
                      alignItems="center"
                      gap="4px"
                      display="inline-flex"
                      maxWidth="100%"
                    >
                      {/*
                        表示名 (取引先名 / 市場 market_kind ラベル) は市場・契約
                        共通で 1 つだけ描画する (旧実装は market/contract 分岐の
                        両方に同じ Box/Text マークアップを複製していた)。契約
                        固有の下段 2 行目 (AC-S4D-039: 取引ID を取引先名の下段に
                        出す 2 段見出し、design/supply/48data.html の
                        .s48-th-deal 準拠) と列単位リセットだけを
                        `!isMarket` で gate する (市場列は AC-S4D-032 により
                        いずれも持たない — Decisions #5)。下段に出すのは相対取引
                        契約管理と照合できる取引ID (public_trade_id)。成約ID
                        (deal_id) は照合キーではないため見出しには出さない (#6697)。
                      */}
                      <Box flex={1} overflow="hidden" minWidth={0}>
                        {/*
                          AC-S4D-039a: 列幅は固定 (7rem、S48_DATA_COL_WIDTH_PX)
                          とし、取引先マスタの略称が長い場合は省略 (ellipsis) せず
                          2 行見出しの中で折り返す。`overflowWrap="anywhere"` が
                          無いと空白の無い英数字混じりの略称 (アルファベット企業名等)
                          が折り返せず列幅を押し広げてしまう
                          (DemandForecastDaily/components/KomaGrid.tsx
                          SourceSubLabel と同じ理由・同じ対処)。
                        */}
                        <Text
                          as="span"
                          display="block"
                          whiteSpace="normal"
                          overflowWrap="anywhere"
                        >
                          {inputMissingLegIds?.has(leg.supply48SourceId) ===
                            true && (
                            // eslint-disable-next-line ebalance/no-adhoc-typography -- design .s48-th-warn (xs/700) 準拠。warning 色の枠・文字の小さなチップで、size と weight の両方が要る
                            <Text
                              as="span"
                              display="inline-block"
                              marginRight="4px"
                              color="Text.warningHigh"
                              fontSize="xs"
                              fontWeight="bold"
                              lineHeight="1"
                              border="1px solid"
                              borderColor="Border.warningMid"
                              borderRadius="sm"
                              whiteSpace="nowrap"
                              backgroundColor="Surface.primary"
                              data-testid="input-missing-badge"
                              paddingX="4px"
                              paddingY="2px"
                              title={t(
                                'page:supply48Data.inputMissing.badgeTitle',
                              )}
                            >
                              {t('page:supply48Data.inputMissing.badge')}
                            </Text>
                          )}
                          {label}
                        </Text>
                        {!isMarket && (
                          <>
                            {tradeType === 'backupRegular' && (
                              // eslint-disable-next-line ebalance/no-adhoc-typography -- design .s48-th-deal-id (xs/400) 準拠。th が fontWeight:600 を持つため、下段を「ラベル < データ」の階層に落とすには size と weight の両方が要る
                              <Text
                                as="span"
                                display="block"
                                color="Text.midEmphasis"
                                fontSize="xs"
                                fontWeight="normal"
                              >
                                {tradeTypeLabel}
                              </Text>
                            )}
                            {/* public_trade_id は契約 source で常に存在するので空ガードは置かない */}
                            {/* eslint-disable-next-line ebalance/no-adhoc-typography -- 同上 (design .s48-th-deal-id) */}
                            <Text
                              as="span"
                              display="block"
                              color="Text.midEmphasis"
                              fontSize="xs"
                              fontWeight="normal"
                              fontVariantNumeric="tabular-nums"
                            >
                              {publicTradeId}
                            </Text>
                          </>
                        )}
                      </Box>
                      {/*
                        列単位で自動計算値に戻す。
                        AC-S4D-008: 手動変更が 1 件以上ある列にのみ出す
                        (市場列は保存対象外のため手動変更が生じず対象外)。
                        AC-S4D-001 (issue #5129): 先物カテゴリも保有量確認のみで
                        revert 対象外 (先物 leg は CONTRACT source のため isMarket
                        では弾けない — cellAt / locked と同じ isFuturesCategory
                        で塞ぐ)。
                      */}
                      {!isMarket &&
                        !isFuturesCategory &&
                        canEdit &&
                        onRevertLegToAuto !== undefined &&
                        (manualEditCountByLeg.get(leg.supply48SourceId) ?? 0) >
                          0 && (
                          <RevertButton
                            legId={leg.supply48SourceId}
                            onRevertLegToAuto={onRevertLegToAuto}
                            title={t(
                              'page:supply48Data.toolbar.revertLegToAuto',
                              {
                                count:
                                  manualEditCountByLeg.get(
                                    leg.supply48SourceId,
                                  ) ?? 0,
                                leg: label,
                              },
                            )}
                          />
                        )}
                    </Flex>
                  </th>
                </HintTooltip>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {/*
           * 1 行 = 受渡日 × 1 コマ。行内に全 leg 分のデータセルを並べる。
           */}
          {visibleDates.map((date, dateIdx) => {
            const dateKey = toDateKey(date)
            const dateLabel = protoDateToShortLabel(date)

            return Array.from({ length: KOMA_COUNT }, (_, komaIdx) => {
              const komaNumber = komaIdx + 1
              const komaLabelStr = KOMA_TIME_LABELS[komaIdx]
              /* 行 zebra (デザイナー指摘 2026-08-28「各行に交互に色を入れて視認性を
                 上げる UI も 8080 準拠」)。 8080 は
                 `.table-48 tbody tr:nth-child(even) td { background: --surface-secondary }`
                 で **行単位**に縞を敷く。 表全体を通した通し番号で数えるので、
                 日付をまたいでも縞が途切れない。 */
              const isEvenRow = (dateIdx * KOMA_COUNT + komaIdx) % 2 === 1
              /* `Surface.secondary` (#f8f8f8) は 8080 の行背景の実測値
                 (rgb(248,248,248)) と一致する。 sticky セル (日付 / コマ列) は
                 tr の背景を継承できないので、 同じ値を自分で塗る。 */
              const rowBackground = isEvenRow
                ? LightModeSemanticColors.Surface.secondary.value
                : LightModeSemanticColors.Surface.primary.value

              return (
                <tr
                  key={`date-${dateKey}-koma-${komaNumber}`}
                  style={{ background: rowBackground }}
                >
                  {/* 日付列: komaIdx=0 のときのみ rowSpan=48 で表示 */}
                  {komaIdx === 0 && (
                    <td
                      rowSpan={KOMA_COUNT}
                      style={{
                        ...STICKY_COL_STYLES,
                        left: 0,
                        width: 64,
                        minWidth: 64,
                        zIndex: 2,
                        textAlign: 'center',
                        fontWeight: 500,
                        borderBottom: `2px solid ${COLOR_BORDER_LOW}`,
                        borderRight: `1px solid ${COLOR_BORDER_LOW}`,
                        padding: '4px 8px',
                        // sticky セルは tr の背景を継承できないので、 行 zebra と同じ
                        // 色を自分で塗る (8080 の
                        // `tr:nth-child(even) td:nth-child(1)` と同じ扱い)。
                        background: rowBackground,
                        fontSize: fontSizes.md.value,
                      }}
                    >
                      {dateLabel}
                    </td>
                  )}

                  {/* コマ列 */}
                  <td
                    style={{
                      ...STICKY_COL_STYLES,
                      left: 64,
                      width: 60,
                      minWidth: 60,
                      zIndex: 2,
                      textAlign: 'center',
                      // 8080 `.table-48 tbody td:nth-child(2) { font-weight: 700 }`
                      // (2026-07-09 ユーザー指定「コマ列は bold で行の識別性を上げる」)。
                      fontWeight: 700,
                      // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の
                      // 区切り線)。 border-collapse: separate なので各セルの下線で引く。
                      borderBottom: isKomaDividerAfter(komaNumber)
                        ? KOMA_DIVIDER_BORDER
                        : `1px solid ${COLOR_BORDER_LOW}`,
                      borderRight: `1px solid ${COLOR_BORDER_LOW}`,
                      padding: '4px 8px',
                      // sticky セルは tr の背景を継承できないので、 行 zebra と同じ
                      // 色を自分で塗る (8080 の
                      // `tr:nth-child(even) td:nth-child(2)` と同じ扱い)。
                      background: rowBackground,
                      fontSize: fontSizes.md.value,
                    }}
                  >
                    {komaLabelStr}
                  </td>

                  {/* 全 leg (source) 分のデータセル */}
                  {legs.map((leg, legIdx) => {
                    const gridRow = dateIdx * KOMA_COUNT + komaIdx
                    const rowKey = makeEditRowKey(
                      leg.supply48SourceId,
                      date.year,
                      date.month,
                      date.day,
                    )
                    const day = dayIndex.get(rowKey)
                    const editRow = editState.get(rowKey)

                    // 保存単位 (kWh/30min) の値。契約値超過の閾値判定はこちらで行う
                    const storedValue = displaySlotValue(
                      day,
                      editRow?.slots,
                      komaIdx,
                    )

                    // AC-S4D-032: 編集可能でない source (allow-list、現状
                    // CONTRACT のみ) は GC 状態に関わらず常時 locked 表現
                    // (既存 locked セル表現を流用、Design Note UI§Design gaps)。
                    // cellAt の editable 判定と同じ isEditableSource を使い、
                    // 市場以外の非編集 source (GENERATION 等) も書き込み拒否と
                    // 見た目が食い違わないよう fail-closed にする。AC-S4D-001:
                    // 先物カテゴリも cellAt と同じ isFuturesCategory で locked にする
                    // (保有量確認のみ、issue #5129)。
                    const locked =
                      isCellLocked(gateClose, day, date, komaNumber) ||
                      !isEditableSource(leg) ||
                      isFuturesCategory
                    // AC-S4D-049: セル状態 3 種別。「編集中」「変更後」は独立レイヤ
                    // (isKomaEditing / isKomaSavedManual の docstring 参照)。
                    const isEditing = isKomaEditing(
                      editRow,
                      day?.slots[komaIdx]?.value ?? null,
                      komaNumber,
                      // 非定型の取引の列は印の違いも未保存 (AC-S4D-060)
                      nonStandardLegIds.has(leg.supply48SourceId)
                        ? (day?.manualEditKomas ?? [])
                        : undefined,
                    )
                    const isSavedManual = isKomaSavedManual(
                      editRow,
                      day?.manualEditKomas ?? [],
                      komaNumber,
                    )
                    // 「エラー」は直前の保存試行の結果 (index.tsx の
                    // handleSave が Save 押下のたびに作る saveErrorCells) を
                    // そのまま見る。 render のたび (= 1 打鍵ごと) に
                    // checkSavableStoredValue を呼ばない理由は
                    // collectSaveErrorCells の docstring 参照 (入力途中の値を
                    // 誤ってエラー扱いしてしまう)。 保存済みの値へ戻したセルは
                    // 拒否された値をもう持たないので、 編集中のセルに限る。
                    const saveErrorReason = isEditing
                      ? saveErrorCells.get(
                          makeSaveErrorCellKey(rowKey, komaNumber),
                        )
                      : undefined
                    const saveErrorTooltip =
                      saveErrorReason !== undefined
                        ? t(saveErrorTooltipKey(saveErrorReason))
                        : undefined

                    const { contractVolumeMw } = contractDetail(leg)
                    // 契約電力量は kWh/30min 基準のため、表示単位に引きずられないよう
                    // 保存単位のまま突き合わせる。市場列は contractVolumeMw が常に
                    // undefined (contractDetail のフォールバック) のため常に false。
                    const isOverContract = isOverContractThreshold(
                      storedValue,
                      contractVolumeMw,
                    )
                    const cellLabel = sourceDisplayLabel(leg, t)
                    // AC-S4D-052: 遷移直後の一時列ハイライト対象か (th と同じ判定)。
                    const isScrollHighlighted =
                      scrollHighlightedLegId === leg.supply48SourceId

                    return (
                      <GridCell
                        key={`${rowKey}-koma-${komaNumber}`}
                        row={gridRow}
                        col={legIdx}
                        columnLabel={cellLabel}
                        dateLabel={dateLabel}
                        displayValue={toDisplayValue(storedValue, displayUnit)}
                        enabled={canEdit}
                        isEditing={isEditing}
                        isKomaDividerRow={isKomaDividerAfter(komaNumber)}
                        isOverContract={isOverContract}
                        isSavedManual={isSavedManual}
                        isScrollHighlighted={isScrollHighlighted}
                        isSelected={isInRange(selection, gridRow, legIdx)}
                        isZebraRow={isEvenRow}
                        komaLabel={komaLabelStr ?? ''}
                        locked={locked}
                        onChange={onCellInputChange}
                        saveErrorTooltip={saveErrorTooltip}
                      />
                    )
                  })}
                </tr>
              )
            })
          })}
        </tbody>
      </table>
    </Box>
  )
}
